import { useCallback, useEffect, useLayoutEffect, useSyncExternalStore } from 'react';
import type { SidebarState } from '@till-erp/platform/user-values';
import { BP_DESKTOP, BP_TABLET } from './breakpoints.ts';

/**
 * Bildschirmgrößen und Rahmen-Zustand (Change 03.10.2026 „Handy und Tablet“ und Design E · final, Rudolf;
 * EXPERIENCE §4.1, §10; DESIGN Layout & Spacing). Grenzen wie in styles.css (`tablet:` / `desktop:`).
 */
export type Screen = 'phone' | 'tablet' | 'desktop';

const TABLET_QUERY = `(min-width: ${BP_TABLET}px)`;
const DESKTOP_QUERY = `(min-width: ${BP_DESKTOP}px)`;

function currentScreen(): Screen {
  if (typeof window === 'undefined' || !window.matchMedia) return 'desktop';
  if (window.matchMedia(DESKTOP_QUERY).matches) return 'desktop';
  if (window.matchMedia(TABLET_QUERY).matches) return 'tablet';
  return 'phone';
}

function subscribeScreen(listener: () => void): () => void {
  const queries = [window.matchMedia(TABLET_QUERY), window.matchMedia(DESKTOP_QUERY)];
  queries.forEach((q) => q.addEventListener('change', listener));
  return () => queries.forEach((q) => q.removeEventListener('change', listener));
}

/** Handy (unter 768 px), Tablet (768–1279 px) oder Desktop (ab 1280 px); wechselt ohne Neuladen. */
export function useScreen(): Screen {
  return useSyncExternalStore(subscribeScreen, currentScreen, () => 'desktop');
}

// ---------------------------------------------------------------------------
// Seitenleiste am Desktop: Profilwert der Person; localStorage nur Zwischenspeicher bis das Profil geladen ist.

const SIDEBAR_KEY = 'till-erp.sidebar';
let sidebar: SidebarState = readStoredSidebar();
let sidebarSaver: ((next: SidebarState) => void) | null = null;
const sidebarListeners = new Set<() => void>();

// Ohne gemerkten Wert die schmale Symbolleiste (Change 11.10.2026 Design S3: Standard).
function readStoredSidebar(): SidebarState {
  try {
    return window.localStorage.getItem(SIDEBAR_KEY) === 'expanded' ? 'expanded' : 'collapsed';
  } catch {
    return 'collapsed';
  }
}

function applySidebar() {
  // Breite des Inhalts links (`--content-left`) folgt per CSS aus diesem Merkmal (nur am Desktop).
  // Ohne DOM (Komponententests in Node, die Popover über die Listenkarte laden) nur die Hörer benachrichtigen.
  if (typeof document !== 'undefined') document.documentElement.dataset.sidebar = sidebar;
  sidebarListeners.forEach((l) => l());
}

function subscribeSidebar(listener: () => void): () => void {
  sidebarListeners.add(listener);
  return () => {
    sidebarListeners.delete(listener);
  };
}

/** Zustand setzen; `persist` speichert ihn zusätzlich im Profil (wenn angemeldet). */
export function setSidebar(next: SidebarState, options: { persist?: boolean } = {}): void {
  sidebar = next;
  try {
    window.localStorage.setItem(SIDEBAR_KEY, next);
  } catch {
    // Ohne Speicher gilt der Zustand bis zum Neuladen; das Profil bleibt maßgeblich.
  }
  applySidebar();
  if (options.persist !== false) sidebarSaver?.(next);
}

/** App-Hülle: Zustand aus dem Profil übernehmen und Änderungen ins Profil speichern. */
export function useProfileSidebar(profileSidebar: SidebarState, save: (next: SidebarState) => void): void {
  useEffect(() => {
    setSidebar(profileSidebar, { persist: false });
  }, [profileSidebar]);
  useEffect(() => {
    sidebarSaver = save;
    return () => {
      if (sidebarSaver === save) sidebarSaver = null;
    };
  }, [save]);
}

applySidebar();

/** Gemerkter Zustand der Seitenleiste am Desktop und Umschalten (speichert im Profil). */
export function useSidebar(): { sidebar: SidebarState; toggle: () => void } {
  const current = useSyncExternalStore(subscribeSidebar, () => sidebar);
  const toggle = useCallback(() => setSidebar(sidebar === 'collapsed' ? 'expanded' : 'collapsed'), []);
  return { sidebar: current, toggle };
}

// ---------------------------------------------------------------------------
// Seitentitel für die Kopfzeile am Handy: `PageHeader` meldet ihn hier an.

let pageTitle = '';
const titleListeners = new Set<() => void>();

function subscribeTitle(listener: () => void): () => void {
  titleListeners.add(listener);
  return () => {
    titleListeners.delete(listener);
  };
}

/** `PageHeader` meldet seinen Titel an, solange er sichtbar ist. */
export function usePublishPageTitle(title: string): void {
  useLayoutEffect(() => {
    pageTitle = title;
    titleListeners.forEach((l) => l());
    return () => {
      if (pageTitle === title) {
        pageTitle = '';
        titleListeners.forEach((l) => l());
      }
    };
  }, [title]);
}

/** Titel der aktuellen Seite (leer, solange keine Seite einen Kopf zeigt). */
export function usePageTitle(): string {
  return useSyncExternalStore(subscribeTitle, () => pageTitle);
}

/**
 * HS 04.11.2026 (N42): Passt der Seitentitel in der Handy-Kopfzeile nicht einmal mit 8 Zeichen, blendet die Kopfzeile ihn
 * aus – dann zeigt `PageHeader` seine Überschrift sichtbar, auch bei `phoneTitleInBar`. Genau ein Ort zeigt den Titel.
 */
let barTitleHidden = false;
const barTitleListeners = new Set<() => void>();
export function setBarTitleHidden(hidden: boolean): void {
  if (barTitleHidden === hidden) return;
  barTitleHidden = hidden;
  barTitleListeners.forEach((l) => l());
}
export function useBarTitleHidden(): boolean {
  return useSyncExternalStore((listener) => { barTitleListeners.add(listener); return () => { barTitleListeners.delete(listener); }; }, () => barTitleHidden);
}
